/**
 * How tall a directory row is.
 *
 * Compact is the desk reading a long list; roomy is the desk reading one
 * row at a time. It is the reader's choice, remembered in this browser the
 * same way the column set is — a convenience, never load-bearing: storage
 * that is missing or refused falls back to compact, not to no table.
 */
import { useEffect, useState } from 'react'
import clsx from 'clsx'

export type ContactDensity = 'compact' | 'roomy'

const STORAGE_KEY = 'cmos.crm.density.v1'
const DEFAULT_DENSITY: ContactDensity = 'compact'

function isDensity(value: unknown): value is ContactDensity {
  return value === 'compact' || value === 'roomy'
}

export function useContactDensity(): [ContactDensity, (next: ContactDensity) => void] {
  const [density, setDensity] = useState<ContactDensity>(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      return isDensity(raw) ? raw : DEFAULT_DENSITY
    } catch {
      return DEFAULT_DENSITY
    }
  })

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, density)
    } catch {
      /* storage unavailable — the choice lives for this session only */
    }
  }, [density])

  return [density, setDensity]
}

/** The toolbar control: two states, one pressed. Same shape as the pipeline's
 *  Dense / Roomy switch so the two screens read as one product. */
export function DensityToggle({
  density,
  onChange,
}: {
  density: ContactDensity
  onChange: (next: ContactDensity) => void
}) {
  return (
    <div className="inline-flex rounded-md border border-amber-400/10 bg-black/40 p-0.5" role="group" aria-label="Row density">
      {(
        [
          ['compact', 'Compact'],
          ['roomy', 'Roomy'],
        ] as const
      ).map(([mode, label]) => (
        <button
          key={mode}
          type="button"
          aria-pressed={density === mode}
          onClick={() => onChange(mode)}
          title={mode === 'compact' ? 'Shorter rows, more of the directory on screen' : 'Taller rows, easier to read one at a time'}
          className={clsx(
            'rounded px-2 py-0.5 text-[11px] font-medium transition-colors',
            density === mode ? 'bg-amber-400/15 text-amber-200' : 'text-stone-400 hover:text-stone-200',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  )
}
